import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@/components/ui';

/**
 * Schluesselwort der verschaerften Bestaetigung (Spec 510 D-9): statisch, case-sensitiv,
 * in DE und EN identisch (kein uebersetztes Schluesselwort — Doku/Screenshots bleiben
 * sprachuebergreifend gueltig). Vergleich nach trim().
 */
export const ROTATE_CONFIRM_KEYWORD = 'ROTATE';

interface RotateSaltConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantName: string;
  onConfirm: () => void;
  isPending: boolean;
  /**
   * Spec 510 D-7/D-9 (verschaerfter Confirm fuer Merchant-Rollen): der Bestaetigungs-Button
   * bleibt deaktiviert, bis das Schluesselwort ROTATE exakt (case-sensitiv, getrimmt)
   * eingetippt wurde. Ohne die Prop verhaelt sich der Dialog wie bisher (Drawer/Platform).
   */
  requireTypedConfirmation?: boolean;
}

/**
 * Salt-Rotation-Confirm (Spec 497 Inc 2, D-Components; Spec 510 D-9 Tipp-Bestaetigung).
 *
 * Einstiegs-agnostisch (nur Props). Warnt explizit, dass bereits verteilte Links beim naechsten
 * Verify ungueltig werden. Der Bestaetigungs-Button ist waehrend `isPending` deaktiviert
 * (Doppel-Rotation-Schutz, OQ-4-Mitigation). Wiederverwendbar in Inc 2b.
 */
export function RotateSaltConfirm({
  open,
  onOpenChange,
  merchantName,
  onConfirm,
  isPending,
  requireTypedConfirmation = false,
}: RotateSaltConfirmProps) {
  const { t } = useTranslation('payment-link');
  const [typed, setTyped] = useState('');

  // Beim Schliessen leeren — ein erneutes Oeffnen startet immer mit leerem Feld
  // (keine "vorbestaetigte" Rotation aus einem frueheren Dialog-Durchlauf).
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const typedConfirmationMissing =
    requireTypedConfirmation && typed.trim() !== ROTATE_CONFIRM_KEYWORD;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('confirm.rotateTitle')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('confirm.rotateBody', { merchant: merchantName })}
          </DialogDescription>
        </DialogHeader>

        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" aria-hidden="true" />
          <p className="text-sm font-medium text-warning-foreground">
            {t('confirm.rotateBody', { merchant: merchantName })}
          </p>
        </div>

        {requireTypedConfirmation && (
          <div className="space-y-2">
            <Label htmlFor="rotate-typed-confirm">
              {t('confirm.rotateTypedLabel', { keyword: ROTATE_CONFIRM_KEYWORD })}
            </Label>
            {/* Bewusst KEIN type="password" — das Schluesselwort ist kein Geheimnis (D-9). */}
            <Input
              id="rotate-typed-confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              disabled={isPending}
            />
            <p className="text-xs text-muted-foreground">
              {t('confirm.rotateTypedHint', { keyword: ROTATE_CONFIRM_KEYWORD })}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('confirm.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending || typedConfirmationMissing}
          >
            {t('confirm.rotateSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
